import { useMemo, useRef } from 'react';
import classNames from 'classnames';

import { DashboardWebsite } from '../../types/types';
import { OverallHealth } from '../../types/enums';

import dummyBg from '../DashboardFooter/logo.png';

import styles from './Dashboard.module.scss';

interface DashboardProps {
  resources: DashboardWebsite[];
}

const Dashboard = ({ resources }: DashboardProps) => {
  return resources.length > 18 ? (
    <Loop itemCount={resources.length}>
      <Cards resources={resources} />
    </Loop>
  ) : (
    <Cards resources={resources} />
  );
};

const healthClasses = {
  [OverallHealth.ERROR]: styles.errorState,
  [OverallHealth.WARNING]: styles.warningState,
  [OverallHealth.OK]: styles.okState,
  [OverallHealth.UNKNOWN]: styles.loadingState,
  [OverallHealth.UNAVAILABLE]: styles.unavailableState
};

const healthLabel = {
  [OverallHealth.ERROR]: 'error',
  [OverallHealth.WARNING]: 'warning',
  [OverallHealth.OK]: 'ok',
  [OverallHealth.UNKNOWN]: 'loading',
  [OverallHealth.UNAVAILABLE]: 'unavailable'
};

const Cards: React.FC<{
  resources: DashboardWebsite[];
}> = ({ resources }) => {
  const orderedResources = useMemo(
    () => resources.sort((a, b) => a.name.localeCompare(b.name)),
    [resources]
  );

  return (
    <div className={styles.cards}>
      {orderedResources.map((site) => (
        <div key={site.id} className={classNames(styles.card, healthClasses[site.overallHealth])}>
          <div>
            <div className={styles.siteTitle}>{site.name}</div>
            <div className={styles.details}>
              <div className={styles.statusWrapper}>
                <strong>Status:</strong>
                <div className={styles.health}>{healthLabel[site.overallHealth]}</div>
              </div>
              {site.statusText && <div className={styles.statusDetail}>{site.statusText}</div>}
            </div>
          </div>
          <div
            style={{
              backgroundImage: site.iconPath
                ? `url("${process.env.REACT_APP_ASSETS_CDN + site.iconPath}")`
                : `url("${dummyBg}")`
            }}
            className={styles.siteImage}></div>
        </div>
      ))}
    </div>
  );
};

const Loop = ({ children, itemCount }: React.PropsWithChildren & { itemCount: number }) => {
  const cardsRef = useRef<HTMLDivElement | null>();

  return (
    <div
      ref={(el) => (cardsRef.current = el)}
      style={
        {
          animationDuration: `${8 * (itemCount - 18)}s`,
          '--to-animate': `${cardsRef.current?.scrollHeight}px`
        } as React.CSSProperties
      }
      className={cardsRef.current ? styles.verticalLoop : ''}>
      {children}
      {children}
    </div>
  );
};

export default Dashboard;
